import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/PasswordInput";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import { SocialAuthButtons } from "../components/SocialAuthButtons";

const LoginPage = () => {
  const { t } = useTranslation();
  const { login, isLoading } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await login({ email, password });
      toast({
        title: t("auth.login.successTitle"),
        description: t("auth.login.successDescription"),
        variant: "success",
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const rawMessage =
        typeof err === "string"
          ? err
          : err instanceof Error
          ? err.message
          : t("auth.login.errorDescription");
      const message =
        rawMessage === "Invalid credentials"
          ? t("auth.login.invalidCredentials")
          : rawMessage;
      toast({
        title: t("auth.login.errorTitle"),
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <section className="mx-auto max-w-md space-y-6 rounded-3xl border border-border bg-card p-8 text-foreground shadow-lg dark:border-white/10 dark:bg-white/5 dark:text-white">
      <SocialAuthButtons
        onSuccess={() => navigate("/dashboard", { replace: true })}
      />
      <div>
        <h2 className="text-2xl font-semibold">{t("auth.login.title")}</h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">{t("auth.login.subtitle")}</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.login.emailLabel")}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.login.passwordLabel")}</Label>
          <PasswordInput
            id="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>
        <div className="text-right">
          <Link
            to="/forgot-password"
            className="text-sm text-primary underline hover:text-primary/80 dark:text-white/70 dark:hover:text-white">
            {t("auth.login.forgotPassword")}
          </Link>
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? t("auth.login.submitting") : t("auth.login.submit")}
        </Button>
      </form>
    </section>
  );
};

export default LoginPage;
